let transformer;
let renderer;

// ============================================
// MEASUREMENT RENDERER CLASS
// Handles all rendering logic for measurement lines on SVG
// ============================================

    class MeasurementRenderer {
        constructor(svgElement, imageElement) {
            this.svg = svgElement;
            this.image = imageElement;
            this.measurements = [];
            this.isVisible = false;
        }

        // Set measurements configuration
        setMeasurements(measurements) {
            this.measurements = measurements;
            this.render();
        }

        // Update a specific measurement
        updateMeasurement(handle, updates) {
            const measurement = this.measurements.find(m => m.handle === handle);
            if (measurement) {
            Object.assign(measurement, updates);
            this.render();
            }
        }

        // Easing function for smooth animation
        easeOutCubic(t) {
            return 1 - Math.pow(1 - t, 3);
        }

        // Animate end point along the line
        animateEndPoints() {
            const endPoints = this.svg.querySelectorAll('circle.end-point');
            const duration = 300; // milliseconds
            const startTime = performance.now();

            const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const linearProgress = Math.min(elapsed / duration, 1);
            const progress = this.easeOutCubic(linearProgress);

            endPoints.forEach(circle => {
                const x1 = parseFloat(circle.style.getPropertyValue('--start-x'));
                const y1 = parseFloat(circle.style.getPropertyValue('--start-y'));
                const x2 = parseFloat(circle.style.getPropertyValue('--end-x'));
                const y2 = parseFloat(circle.style.getPropertyValue('--end-y'));

                const currentX = x1 + (x2 - x1) * progress;
                const currentY = y1 + (y2 - y1) * progress;

                circle.setAttribute('cx', currentX);
                circle.setAttribute('cy', currentY);
            });

            if (linearProgress < 1) {
                requestAnimationFrame(animate);
            }
            };

            requestAnimationFrame(animate);
        }

        // Toggle visibility of all measurements with animation
        toggleVisibility() {
            this.isVisible = !this.isVisible;

            if (this.isVisible) {
            // Show with animation
            this.render();
            this.svg.classList.remove('hiding');
            this.svg.classList.add('animating');

            // Animate end points along the lines
            this.animateEndPoints();

            // Remove animation class after animation completes
            setTimeout(() => {
                this.svg.classList.remove('animating');
            }, 600);
            } else {
            // Hide with animation
            this.svg.classList.remove('animating');
            this.svg.classList.add('hiding');

            // Clear SVG after animation completes
            setTimeout(() => {
                this.svg.innerHTML = "";
                this.svg.classList.remove('hiding');
            }, 400);
            }

            return this.isVisible;
        }

        // Calculate line endpoints based on configuration
        calculateLineEndpoints(line) {
            const rect = this.image.getBoundingClientRect();
            const centerX = rect.width * (line.start_point_x / 100);
            const centerY = rect.height * (line.start_point_y / 100);

            let x1 = centerX;
            let y1 = centerY;
            let x2 = centerX;
            let y2 = centerY;

            // Calculate endpoints based on direction
            if (line.direction_of_displacement === "rightwards") {
            x2 = centerX + rect.width * (line.displacement_x / 100);
            if (line.displacement_y) {
                y2 = centerY + rect.height * (line.displacement_y / 100);
            }
            } else if (line.direction_of_displacement === "upwards") {
            y2 = centerY - rect.height * (line.displacement_y / 100);
            if (line.displacement_x) {
                x2 = centerX + rect.width * (line.displacement_x / 100);
            }
            }

            // Apply tilt rotation if specified
            const tilt = line.tilt || 0;
            if (tilt !== 0) {
            const angleRad = (tilt * Math.PI) / 180;
            const cos = Math.cos(angleRad);
            const sin = Math.sin(angleRad);

            // Rotate point 1
            const dx1 = x1 - centerX;
            const dy1 = y1 - centerY;
            x1 = centerX + dx1 * cos - dy1 * sin;
            y1 = centerY + dx1 * sin + dy1 * cos;

            // Rotate point 2
            const dx2 = x2 - centerX;
            const dy2 = y2 - centerY;
            x2 = centerX + dx2 * cos - dy2 * sin;
            y2 = centerY + dx2 * sin + dy2 * cos;
            }

            return { x1, y1, x2, y2 };
        }

        // Create SVG element
        createSVGElement(type, attrs = {}) {
            const el = document.createElementNS("http://www.w3.org/2000/svg", type);
            Object.keys(attrs).forEach(key => el.setAttribute(key, attrs[key]));
            return el;
        }

        // Draw a single measurement line
        drawMeasurementLine(line) {
            if (line.is_visible === false) return;

            const { x1, y1, x2, y2 } = this.calculateLineEndpoints(line);

            // Calculate line length and angle for animation
            const lineLength = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
            const angle = Math.atan2(y2 - y1, x2 - x1);

            // Main line with length for animation
            const lineEl = this.createSVGElement("line", { x1, y1, x2, y2 });
            lineEl.style.setProperty('--line-length', lineLength);
            this.svg.appendChild(lineEl);

            // Start point circle (always visible)
            const startCircle = this.createSVGElement("circle", {
            cx: x1, cy: y1, r: 3, class: "start-point"
            });
            this.svg.appendChild(startCircle);

            // End point circle (animates along the line)
            // Start at x1, y1 for animation, will move to x2, y2
            const endCircle = this.createSVGElement("circle", {
            cx: x1, cy: y1, r: 3, class: "end-point"
            });
            endCircle.style.setProperty('--start-x', x1);
            endCircle.style.setProperty('--start-y', y1);
            endCircle.style.setProperty('--end-x', x2);
            endCircle.style.setProperty('--end-y', y2);
            this.svg.appendChild(endCircle);

            // Label at midpoint
            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2;
            const labelText = `${line.value} ${line.unit}`;

            // Measure text
            const tempText = this.createSVGElement("text", { class: "label" });
            tempText.textContent = labelText;
            this.svg.appendChild(tempText);
            const bbox = tempText.getBBox();
            this.svg.removeChild(tempText);

            // Label background - centered at midpoint
            this.svg.appendChild(this.createSVGElement("rect", {
            class: "label-bg",
            x: midX - bbox.width / 2 - 8,
            y: midY - bbox.height / 2 - 4,
            width: bbox.width + 16,
            height: bbox.height + 8
            }));

            // Label text - centered at midpoint
            const label = this.createSVGElement("text", {
            class: "label",
            x: midX,
            y: midY + 4,
            "text-anchor": "middle"
            });
            label.textContent = labelText;
            this.svg.appendChild(label);
        }

        // Main render method
        render() {
            if (!this.isVisible) return;

            this.svg.innerHTML = "";

            // Ensure clean state for regular renders (e.g., on resize)
            const hasAnimationClass = this.svg.classList.contains('animating');
            if (!hasAnimationClass) {
            this.svg.classList.remove('hiding');
            }

            this.measurements.forEach(line => this.drawMeasurementLine(line));

            // If not animating, set end circles to final positions
            if (!hasAnimationClass) {
            this.svg.querySelectorAll('circle.end-point').forEach(circle => {
                const x2 = parseFloat(circle.style.getPropertyValue('--end-x'));
                const y2 = parseFloat(circle.style.getPropertyValue('--end-y'));
                circle.setAttribute('cx', x2);
                circle.setAttribute('cy', y2);
            });
            }
        }
    }
  
// ============================================
// MEASUREMENT TRANSFORMER CLASS
// Transforms base measurements by applying component overrides
// Manages component selection state and override logic
// ============================================

    class MeasurementTransformer {
        constructor(baseMeasurements, componentOverrides) {
            this.baseMeasurements = baseMeasurements;
            this.componentOverrides = componentOverrides;
            this.selectedComponents = new Set();
        }

        // Apply overrides from selected components to base measurements
        // Overrides are applied ADDITIVELY - values are added to base, not replaced
        transform() {
            // Deep clone base measurements
            const measurements = JSON.parse(JSON.stringify(this.baseMeasurements));

            // Create a map to track cumulative overrides per line
            const cumulativeOverrides = new Map();

            // updatemesurment component selection
            const selections = Object.values(current_state).map(state => state.selection);
            const selectionComponents = selections
            .flatMap(obj => Object.values(obj))
            .flat()
            .filter(Boolean);     
            this.selectedComponents = new Set(selectionComponents);
           

            // Collect all overrides from selected components
            this.selectedComponents.forEach(componentId => {
                const overrides = this.componentOverrides[componentId];
                if (!overrides) return;

                // If overrides is NOT an array → convert it to an array
                if (!Array.isArray(overrides)) {
                    overrides = [overrides];
                }

                overrides.forEach(override => {
                    const targetHandle = override.target_line;
                    
                    if (!cumulativeOverrides.has(targetHandle)) {
                    cumulativeOverrides.set(targetHandle, {
                        new_value: 0,
                        new_center_x: 0,
                        new_center_y: 0,
                        new_displacement_x: 0,
                        new_displacement_y: 0,
                        new_tilt: 0
                    });
                    }

                    const cumulative = cumulativeOverrides.get(targetHandle);

                    // Add override values to cumulative totals
                    if (override.new_value !== undefined) cumulative.new_value += override.new_value;
                    if (override.new_center_x !== undefined) cumulative.new_center_x += override.new_center_x;
                    if (override.new_center_y !== undefined) cumulative.new_center_y += override.new_center_y;
                    if (override.new_displacement_x !== undefined) cumulative.new_displacement_x += override.new_displacement_x;
                    if (override.new_displacement_y !== undefined) cumulative.new_displacement_y += override.new_displacement_y;
                    if (override.new_tilt !== undefined) cumulative.new_tilt += override.new_tilt;
                });
            });

            // Apply cumulative overrides to measurements
            cumulativeOverrides.forEach((cumulative, targetHandle) => {
            const targetLine = measurements.find(m => m.handle === targetHandle);
            if (!targetLine) return;

            this.applyAdditiveOverrides(targetLine, cumulative);
            });

            return measurements;
        }

        // Apply additive override properties to a measurement line
        applyAdditiveOverrides(targetLine, cumulative) {
            const propertyMap = {
            new_value: 'value',
            new_center_x: 'start_point_x',
            new_center_y: 'start_point_y',
            new_displacement_x: 'displacement_x',
            new_displacement_y: 'displacement_y',
            new_tilt: 'tilt'
            };

            Object.entries(propertyMap).forEach(([overrideKey, lineKey]) => {
            if (cumulative[overrideKey] !== 0) {
                targetLine[lineKey] += cumulative[overrideKey];
            }
            });
        }

        // Check if component is selected
        isComponentSelected(componentId) {
            return this.selectedComponents.has(componentId);
        }

        // Get selected components as array
        getSelectedComponents() {
            return Array.from(this.selectedComponents);
        }

        // Clear all selections
        clearSelections() {
            this.selectedComponents.clear();
        }

        // Get base measurements
        getBaseMeasurements() {
            return this.baseMeasurements;
        }

        // Get component overrides
        getComponentOverrides() {
            return this.componentOverrides;
        }
    }
  
// ============================================
// APPLICATION INITIALIZATION & SETUP
// ============================================

    function initializeMeasurementSystem() {
        transformer = new MeasurementTransformer(baseMeasurements, componentOverrides);
        renderer = new MeasurementRenderer(
            document.getElementById("measurements"),
            document.getElementById("bed-background")
        );
    }
// ============================================
// UI CONTROLLER FUNCTIONS
// ============================================
  
// Update measurements based on current component selections
    function updateMeasurements() {
        const transformedMeasurements = transformer.transform();
        renderer.setMeasurements(transformedMeasurements);
    }

// Toggle measurements visibility button
    document.addEventListener('DOMContentLoaded', function () {
        initializeMeasurementSystem();
        renderer.setMeasurements(baseMeasurements);
    
        const toggleButton = document.querySelector(".measurement_trigger_btn");
        if(toggleButton){
        toggleButton.addEventListener("click", () => {
            const isVisible = renderer.toggleVisibility();
            toggleButton.classList.toggle("active_msm_trigger", isVisible);
        });
        }
    });

// Window resize handler
    window.addEventListener("resize", () => renderer.render());
  
// ============================================
// PUBLIC API
// Expose methods for external control
// ============================================
  
    window.measurementAPI = {
        // Renderer methods
        setMeasurements: (measurements) => renderer.setMeasurements(measurements),
        updateMeasurement: (handle, updates) => renderer.updateMeasurement(handle, updates),
        getMeasurements: () => renderer.measurements,
        toggleVisibility: () => renderer.toggleVisibility(),
        render: () => renderer.render(),

        // Transformer methods
        toggleComponent: (componentId) => toggleComponent(componentId),
        getSelectedComponents: () => transformer.getSelectedComponents(),
        isComponentSelected: (componentId) => transformer.isComponentSelected(componentId),
        clearSelections: () => {
            transformer.clearSelections();
            document.querySelectorAll('.component-item').forEach(item => {
            item.classList.remove('selected');
            });
            updateMeasurements();
        },
        getBaseMeasurements: () => transformer.getBaseMeasurements(),
        getComponentOverrides: () => transformer.getComponentOverrides(),
        transform: () => transformer.transform(),

        // Direct access to class instances
        renderer: renderer,
        transformer: transformer
    };
    